import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useElementWidth, useIsNarrow, useReducedMotion } from '../hooks/misc';
import { onFrame } from '../pixel/engine';
import { ForgeRuntime } from '../pixel/forgeScene';
import type { SceneInputs } from '../pixel/forgeScene';
import s from './ForgeScene.module.css';

export interface ForgeEvent {
  key: string;
  lot: number;
  color: string;
}

/**
 * One canvas, drawn at its logical resolution (e.g. 176 x 96) and scaled up by an integer factor
 * with `image-rendering: pixelated`. The logical width follows the container so the scene fills
 * its panel at any size; the layout anchors the furnace left and the ingot staircase right.
 */
export function ForgeScene({
  inputs,
  height = 96,
  mobileHeight = 84,
  scale = 3,
  mobileScale = 2,
  forge,
  proofKey,
  label,
  children,
  testId,
}: {
  inputs: SceneInputs;
  height?: number;
  mobileHeight?: number;
  scale?: number;
  mobileScale?: number;
  forge?: ForgeEvent | null;
  proofKey?: string | null;
  label: string;
  children?: ReactNode;
  testId?: string;
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const narrow = useIsNarrow();
  const reduced = useReducedMotion();
  const k = narrow ? mobileScale : scale;
  const H = narrow ? mobileHeight : height;
  const W = Math.max(160, Math.floor(width / k));
  const canvas = useRef<HTMLCanvasElement>(null);
  const [rt] = useState(() => {
    const r = new ForgeRuntime(W, H);
    r.inputs = inputs;
    return r;
  });
  // the scene follows the props: real launch state only
  rt.inputs = inputs;

  useEffect(() => {
    rt.resize(W, H);
  }, [rt, W, H]);

  // real events -> animations (skip whatever was current at mount)
  const lastForge = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const key = forge?.key ?? null;
    if (lastForge.current !== undefined && key && key !== lastForge.current && forge) rt.forged(forge.lot, forge.color, reduced);
    lastForge.current = key;
  }, [rt, forge, reduced]);
  const lastProof = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastProof.current !== undefined && proofKey && proofKey !== lastProof.current) rt.proof(reduced);
    lastProof.current = proofKey ?? null;
  }, [rt, proofKey, reduced]);

  // the stepped loop (or a single representative frame under reduced motion)
  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx || !width) return;
    if (reduced) {
      rt.setPour(true);
      rt.step(0);
      rt.draw(ctx, 0);
      return;
    }
    return onFrame((f) => {
      rt.step(f);
      rt.draw(ctx, f);
    });
  }, [rt, reduced, width, W, H]);

  // reduced motion: redraw when the state changes (colour changes instead of motion)
  useEffect(() => {
    if (!reduced) return;
    const ctx = canvas.current?.getContext('2d');
    if (ctx && width) rt.draw(ctx, 0);
  });

  return (
    <div ref={ref} className={s.wrap} style={{ height: H * k }} data-testid={testId}>
      <canvas ref={canvas} className={s.canvas} width={W} height={H} style={{ width: W * k, height: H * k }} role="img" aria-label={label} />
      {children ? <div className={s.overlay}>{children}</div> : null}
    </div>
  );
}
